
import React, { useState, useRef, useEffect } from 'react';
import { MdAdd, MdSettings, MdArrowDropDown, MdFilterList, MdBusiness, MdSearch, MdEdit, MdVisibility, MdPerson, MdStore } from 'react-icons/md';
import ReactPaginate from 'react-paginate';
import SearchBox from './SearchBox';
import StatusUpdateModal from './StatusUpdateModal';
import ViewDetailsModal from './ViewDetailsModal';
import TableLoadingWrapper from './TableLoadingWrapper';
import SearchableDropdown from './SearchableDropdown';
import axios from 'axios';
import '../styles/views.css';
import AddTraderModal from './AddTraderModal';
import EditTraderModal from './EditTraderModal';


const TradersView = ({
  pagedFilteredData,
  searchTerm: tradersSearchTerm,
  setSearchTerm: setTradersSearchTerm,
  pageCount,
  handlePageClick,
  currentPage,
  totalRows,
  itemsPerPage,
  statusFilter,
  setStatusFilter,
  mmdaFilter,
  setMmdaFilter,
  showPopup,
  fetchData,
  API_BASE_URL,
}) => {
  const [openPopupIndex, setOpenPopupIndex] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const popupRefs = useRef({});
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedTrader, setSelectedTrader] = useState(null);
  const [isAddTraderModalOpen, setIsAddTraderModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editTrader, setEditTrader] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewTrader, setViewTrader] = useState(null);

  // New states for dropdown data
  const [groups, setGroups] = useState([]);
  const [mmdas, setMmdas] = useState([]);
  const [zones, setZones] = useState([]);
  const [nationalIdTypes, setNationalIdTypes] = useState([]);

  // Fetch all necessary data for the Traders view dropdowns
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

        const [groupsRes, mmdasRes, zonesRes, nationalIdTypesRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/zonal-groups`, config),
          axios.get(`${API_BASE_URL}/mmdas`, config),
          axios.get(`${API_BASE_URL}/zones`, config),
          axios.get(`${API_BASE_URL}/national-ids`, config),
        ]);

        setGroups(groupsRes.data);
        setMmdas(mmdasRes.data);
        setZones(zonesRes.data);
        setNationalIdTypes(nationalIdTypesRes.data);
      } catch (error) {
        console.error('Failed to fetch dropdown data for TradersView:', error);
        showPopup('Failed to load filter options. Please try again.', 'error');
      }
    };

    fetchDropdownData();
  }, [API_BASE_URL, showPopup]);

  // Close popup if click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openPopupIndex !== null) {
        const popup = popupRefs.current[openPopupIndex];
        if (popup && !popup.contains(event.target)) {
          setOpenPopupIndex(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openPopupIndex]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return { bg: '#e8f5e8', color: '#2e7d32' };
      case 'inactive':
        return { bg: '#ffebee', color: '#c62828' };
      case 'suspended':
        return { bg: '#fff3cd', color: '#856404' };
      default:
        return { bg: '#f5f5f5', color: '#666' };
    }
  };

  return (
    <>
      {/* Header Section */}
      <div style={{
        marginBottom: 24,
        borderRadius: 12,
        border: '1px solid #f0f0f0'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <MdStore size={24} style={{ color: '#1976d2' }} />
            <h3 style={{ margin: 0, color: '#333', fontSize: 18, fontWeight: '600' }}>
              Traders Management
            </h3>
            <span style={{
              padding: '4px 12px',
              backgroundColor: '#e3f2fd',
              color: '#1976d2',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '600'
            }}>
              {pagedFilteredData?.length || 0} Traders
            </span>
          </div>



        </div>

        {/* Search and Filter Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1', minWidth: 300 }}>
            <MdSearch size={20} style={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#666'
            }} />
            <input
              type="text"
              placeholder="Search traders by name, business, or phone..."
              value={tradersSearchTerm}
              onChange={(e) => setTradersSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 40px',
                borderRadius: 8,
                border: '1px solid #ddd',
                fontSize: 14,
                outline: 'none',
                transition: 'border-color 0.2s ease',
                boxSizing: 'border-box'
              }}
              onFocus={e => e.target.style.borderColor = '#1976d2'}
              onBlur={e => e.target.style.borderColor = '#ddd'}
            />
          </div>

          {/* Status Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <MdFilterList size={20} style={{ color: '#666' }} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid #ddd',
                fontSize: 14,
                outline: 'none',
                backgroundColor: '#fff',
                cursor: 'pointer',
                minWidth: 120
              }}
            >
              <option value="All">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>

          {/* MMDA Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <MdBusiness size={20} style={{ color: '#666' }} />
            <SearchableDropdown
              options={[
                { value: "All", label: "All MMDAs" },
                ...(mmdas?.map((mmda) => ({ value: mmda.name, label: mmda.name })) || [])
              ]}
              value={mmdaFilter}
              onChange={setMmdaFilter}
              placeholder="All MMDAs"
            />
          </div>
        </div>
      </div>

      {/* Traders Table - MMDA Style */}
      <TableLoadingWrapper loading={false}>
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>Business Name</th>
                <th>MMDA</th>
                <th>Zone</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedFilteredData.map((row, i) => (
                <tr key={i} style={{ cursor: 'default' }}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <strong>{row.business_name}</strong>
                    </div>
                  </td>
                  <td>
                    <span style={{
                      padding: '4px 8px',
                      backgroundColor: '#f3e5f5',
                      borderRadius: 4,
                      fontSize: 12,
                      fontWeight: 'bold',
                      color: '#7b1fa2'
                    }}>
                      {row.mmda_name || 'N/A'}
                    </span>
                  </td>
                  <td>
                    <span style={{
                      padding: '4px 8px',
                      backgroundColor: '#e3f2fd',
                      borderRadius: 4,
                      fontSize: 12,
                      fontWeight: 'bold',
                      color: '#1976d2'
                    }}>
                      {row.zone_name || 'N/A'}
                    </span>
                  </td>
                  <td>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: 4,
                      fontSize: 12,
                      fontWeight: 'bold',
                      backgroundColor: row.status === 'active' ? '#e8f5e8' : '#ffebee',
                      color: row.status === 'active' ? '#2e7d32' : '#c62828'
                    }}>
                      {(row.status || 'active').charAt(0).toUpperCase() + (row.status || 'active').slice(1)}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('authToken');
                            const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
                            const response = await axios.get(`${API_BASE_URL}/traders/${row.trader_id}`, config);
                            setViewTrader(response.data);
                            setIsViewModalOpen(true);
                          } catch (error) {
                            console.error('Failed to fetch trader details:', error);
                            showPopup('Failed to fetch trader details. Please try again.', 'error');
                          }
                        }}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#4caf50',
                          color: 'white',
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: 12,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4
                        }}
                      >
                        <MdVisibility size={14} />
                        View
                      </button>

                      <button
                        onClick={() => {
                          setEditTrader(row);
                          setIsEditModalOpen(true);
                        }}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#ff9800',
                          color: 'white',
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: 12,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4
                        }}
                      >
                        <MdEdit size={14} />
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </TableLoadingWrapper>
      {openPopupIndex !== null && (
        <div
          className="popup"
          ref={(el) => (popupRefs.current[openPopupIndex] = el)}
          style={{
            top: popupPosition.top,
            left: popupPosition.left,
            opacity: openPopupIndex !== null ? 1 : 0,
            transform: openPopupIndex !== null ? 'scale(1)' : 'scale(0.95)',
          }}
        >
          <button
            type="button"
            onClick={() => {
              setEditTrader(pagedFilteredData[openPopupIndex]);
              setIsEditModalOpen(true);
              setOpenPopupIndex(null);
            }}
          >
            Edit
          </button>
          <button type="button" onClick={() => { setOpenPopupIndex(null); }}>
            Status
          </button>
        </div>
      )}
      {/* Enhanced Pagination */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 24,
        padding: '16px 20px',
        backgroundColor: '#f8f9fa',
        borderRadius: 12,
        border: '1px solid #e9ecef'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{
            color: '#495057',
            fontSize: 14,
            fontWeight: '500'
          }}>
            Showing {currentPage * itemsPerPage + 1} to {Math.min((currentPage + 1) * itemsPerPage, totalRows)} of {totalRows} traders
          </span>
          {totalRows > 0 && (
            <span style={{
              padding: '4px 8px',
              backgroundColor: '#e3f2fd',
              color: '#1976d2',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: '600'
            }}>
              Page {currentPage + 1} of {pageCount}
            </span>
          )}
        </div>

        {pageCount > 1 && (
          <ReactPaginate
            previousLabel={'← Previous'}
            nextLabel={'Next →'}
            breakLabel={'...'}
            pageCount={pageCount}
            marginPagesDisplayed={2}
            pageRangeDisplayed={3}
            onPageChange={handlePageClick}
            containerClassName={'pagination'}
            activeClassName={'active'}
            forcePage={currentPage}
          />
        )}
      </div>
      
      <StatusUpdateModal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        group={selectedTrader}
        onStatusUpdate={async (traderId, newStatus) => {
          try {
            const token = localStorage.getItem('authToken');
            const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
            await axios.put(`${API_BASE_URL}/traders/${traderId}/status`, { status: newStatus }, config);
            showPopup('Trader status updated successfully!', 'success');
            fetchData();
          } catch (err) {
            showPopup('Failed to update status. Please try again.', 'error');
          }
        }}
      />

      <AddTraderModal
        isOpen={isAddTraderModalOpen}
        onClose={() => setIsAddTraderModalOpen(false)}
        showPopup={showPopup}
        fetchData={fetchData}
        API_BASE_URL={API_BASE_URL}
      />

      <EditTraderModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        showPopup={showPopup}
        fetchData={fetchData}
        API_BASE_URL={API_BASE_URL}
        editTrader={editTrader}
        groups={groups}
        mmdas={mmdas}
        zones={zones}
        nationalIdTypes={nationalIdTypes}
      />

      {/* View Details Modal */}
      <ViewDetailsModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        data={viewTrader}
        type="trader"
        title="Trader Details"
      />
    </>

  );
};

export default TradersView;
